// src/encounter/sign-note-confirm-dialog.tsx
// Confirmation before signing the encounter note. PRESENTATIONAL — composes the DS Modal + Button.
//
// WHY THIS EXISTS: React fired the sign-and-lock write on a single unguarded click. Flutter confirms
// first, and the reason is in the copy — signing does not only finalise the note, it AUTO-SENDS the
// orders to the patient. That is an outward-facing, effectively irreversible clinical action.
//
// FULL FLUTTER PARITY (Andrew's call, PROD-2012). Every visible property below is Flutter's, taken
// from `confirmDialog` (`scrips_core/lib/widgets/general/alert_dialog.dart:7-125`) as invoked by
// `signAndLockButtonPress` (`generated_notes_widget.dart:452-466`) with strings from
// `getSignLockDialog` (`patient_encounter_bloc.dart:3032-3042`):
//
//   title    "Do you really want to sign this note?"
//   message  "If you sign this note now, your orders will be automatically signed and sent to the
//             patient, too."                                     <- ALWAYS shown, see below
//   buttons  noText FIRST then yesText  =>  [Sign Note] [Cancel]  (alert_dialog.dart:67-110)
//   align    left — plain Row, no leading Spacer                  (:68)
//   dismiss  barrierDismissible: false — a button press is required (:23)
//
// Three of those deliberately differ from this app's other dialogs, so do NOT "normalise" them:
//
//   1. BUTTON ORDER is confirm-first. Flutter renders `noText` before `yesText`, and the sign
//      action is bound to `onNo`. Every ips-*-dialog here is [Cancel] [Primary]; this one is not,
//      by explicit decision. Reversing it back would be a silent parity regression.
//   2. LEFT-ALIGNED. Flutter's button Row has no Spacer, so it starts at the left edge.
//   3. NOT DISMISSIBLE. Flutter forces an explicit choice on this dialog. The DS Modal exposes no
//      dismissal control (its props are open/onClose/title/description/footer/size/children/
//      className), so `onClose` is deliberately INERT here and the two buttons are the only exits.
//      Not a workaround around the DS — the API has no flag, and a no-op close is the honest way to
//      express "must choose". If the DS later grows a `dismissible` prop, switch to it.
//
// The message is UNCONDITIONAL, also by decision. Flutter shows it whenever orders have not been
// sent — including notes with no orders, where it describes something that will not happen. An
// earlier version of this file suppressed the sentence in that case; Andrew chose full parity, so
// the sentence stays. Recorded because it is a knowing trade: strict parity over literal accuracy,
// and Flutter's own dialog cannot render an empty body anyway (`message!` is a non-null assertion).
import { Modal, Button } from '@scripsteam/scrips-signal-ds';

export interface SignNoteConfirmDialogProps {
  open: boolean;
  /** C3 (Andrew's walk, 7 Oct): a nurse's sign sends no orders, so she is never told it does. */
  forNurse?: boolean;
  /** Sign is in flight; keeps the user from double-firing an irreversible write. */
  signing?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const SIGN_CONFIRM_TITLE = 'Do you really want to sign this note?';
export const SIGN_CONFIRM_ORDERS_MESSAGE =
  'If you sign this note now, your orders will be automatically signed and sent to the patient, too.';
/** The nurse's sign (C3): her part of the note is signed; orders stay the physician's. */
export const SIGN_CONFIRM_NURSE_MESSAGE = 'If you sign this note now, your part of it is signed. No orders are signed or sent.';

export function SignNoteConfirmDialog({
  open,
  forNurse = false,
  signing = false,
  onConfirm,
  onCancel,
}: SignNoteConfirmDialogProps) {
  if (!open) return null;
  return (
    <Modal
      open
      // INERT on purpose — Flutter's barrierDismissible: false. Backdrop and Escape must not
      // resolve a confirmation for an irreversible, outward-facing write; Cancel exists for that.
      onClose={() => {}}
      title={SIGN_CONFIRM_TITLE}
      size="sm"
    >
      <div className="font-sans">
        <p className="m-0 text-size-sm leading-prose text-text-primary">
          {forNurse ? SIGN_CONFIRM_NURSE_MESSAGE : SIGN_CONFIRM_ORDERS_MESSAGE}
        </p>
      </div>

      {/* Flutter order + alignment: confirm FIRST, then cancel, starting at the left edge. */}
      <div className="flex justify-start gap-2 mt-4">
        <Button variant="primary" size="sm" onClick={onConfirm} disabled={signing}>
          {signing ? 'Signing…' : 'Sign Note'}
        </Button>
        <Button variant="tertiary" size="sm" onClick={onCancel} disabled={signing}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
